import { StreakSummaryResponse, StreakSummaryResponseV2, AUTOMATIC_STREAK_STATUS } from "@cede/types";
import { isServerApiError, isServerApiRfcError, QueryKeys } from "@cede/utils";
import { useHubUser } from "@/hooks/user/useHubUser";
import { getUserAutomaticStreaks, getUserAutomaticStreaksV2 } from "@/utils/api";
import { useQuery } from "@tanstack/react-query";

/**
 * Unified streak summary type that can hold either V1 or V2 streak summary data.
 * Use the `version` field to discriminate between the two.
 */
export type UnifiedStreakSummary = StreakSummaryResponse | StreakSummaryResponseV2;

export interface UseUserStreaksOptions {
  campaignName?: string;
  enabled?: boolean;
  /**
   * Version of the streak API to use. Defaults to 1 for backward compatibility.
   * Set to 2 for V2 streaks (volume-based instead of cycle-based).
   */
  version?: 1 | 2;
}

export const useUserStreaks = ({ campaignName, enabled = true, version = 1 }: UseUserStreaksOptions = {}) => {
  const { auth, authenticated } = useHubUser();

  const query = useQuery({
    queryKey: [QueryKeys.AUTOMATIC_STREAKS, campaignName, version],
    queryFn: async (): Promise<UnifiedStreakSummary[]> => {
      // Use version-specific API
      const result =
        version === 2
          ? await getUserAutomaticStreaksV2(auth, campaignName)
          : await getUserAutomaticStreaks(auth, campaignName);
      if (isServerApiError(result) || isServerApiRfcError(result)) {
        const errorMessage = isServerApiRfcError(result) ? result.detail : result.message;
        throw new Error(errorMessage);
      }

      return result as UnifiedStreakSummary[];
    },
    enabled: enabled && authenticated,
    staleTime: 10000, // 10 seconds
    refetchInterval: (query) => {
      // If any streak is active, poll more frequently
      const hasActiveStreaks = query.state.data?.some(
        (streak) =>
          streak.status === AUTOMATIC_STREAK_STATUS.PENDING || streak.status === AUTOMATIC_STREAK_STATUS.IN_PROGRESS,
      );
      return hasActiveStreaks ? 10000 : false; // 10 seconds if active, no polling otherwise
    },
  });

  // Derived data helpers
  const activeStreaks =
    query.data?.filter(
      (streak) =>
        streak.status === AUTOMATIC_STREAK_STATUS.PENDING || streak.status === AUTOMATIC_STREAK_STATUS.IN_PROGRESS,
    ) ?? [];

  const completedStreaks = query.data?.filter((streak) => streak.status === AUTOMATIC_STREAK_STATUS.COMPLETED) ?? [];

  const hasActiveStreak = activeStreaks.length > 0;

  return {
    streaks: query.data ?? [],
    activeStreaks,
    completedStreaks,
    hasActiveStreak,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error?.message,
    refetch: query.refetch,
  };
};
